import Decimal from 'decimal.js';
import { decimalFromStr } from "../../infra/utils";
import { serviceBillingModel, serviceModel } from "../../models";
import { PriceIncreaseModel } from "../../models/PriceIncreaseModel";
import { PracticeManagementServiceIntegrationModel } from "../../models/ServiceIntegrations";
import { IncreaseMethod } from "./ServiceAdvancedSettings";
import { AdvancedSetting } from "./ServiceAdvancedSettings.types";
import { defaultIncreaseMethod, defaultIncreaseOn, defaultPrecentIncrease, defaultRoundPrices, getInitialDiscountPeriodExpiration, getInitialLimitedTimeDiscountOffer, getInitialStartBillingAtDate, getValidIncreaseOn, updateStartBillingAtByMenuItem } from "./ServiceAdvancedSettings.utils";
import { StartAutomaticBillingAtControlDropdownMenuItem } from "./StartAutomaticBillingAtControl/StartAutomaticBillingAtControl";
export const AdvancedSettingsActions = {
  [AdvancedSetting.Pause]: {
    activate: _ref => {
      let {
        serviceId,
        onPausedChange
      } = _ref;
      return onPausedChange === null || onPausedChange === void 0 ? void 0 : onPausedChange(serviceId, true);
    },
    deactivate: _ref2 => {
      let {
        serviceId,
        onPausedChange
      } = _ref2;
      return onPausedChange === null || onPausedChange === void 0 ? void 0 : onPausedChange(serviceId, false);
    }
  },
  [AdvancedSetting.BillOnApproval]: {
    activate: _ref3 => {
      let {
        serviceId,
        onBillOnAcceptanceChange
      } = _ref3;
      return onBillOnAcceptanceChange === null || onBillOnAcceptanceChange === void 0 ? void 0 : onBillOnAcceptanceChange(serviceId, true);
    },
    deactivate: _ref4 => {
      let {
        serviceId,
        onBillOnAcceptanceChange
      } = _ref4;
      return onBillOnAcceptanceChange === null || onBillOnAcceptanceChange === void 0 ? void 0 : onBillOnAcceptanceChange(serviceId, false);
    }
  },
  [AdvancedSetting.Prorate]: {
    activate: _ref5 => {
      let {
        serviceId,
        onProratedChange
      } = _ref5;
      return onProratedChange === null || onProratedChange === void 0 ? void 0 : onProratedChange(serviceId, true);
    },
    deactivate: _ref6 => {
      let {
        serviceId,
        onProratedChange
      } = _ref6;
      return onProratedChange === null || onProratedChange === void 0 ? void 0 : onProratedChange(serviceId, false);
    }
  },
  [AdvancedSetting.Optional]: {
    activate: _ref7 => {
      let {
        serviceId,
        service,
        onOptionalChange,
        logBI
      } = _ref7;
      onOptionalChange === null || onOptionalChange === void 0 || onOptionalChange(serviceId, true, false);
      if (!service) return;
      logBI === null || logBI === void 0 || logBI(10488, {
        optional: true,
        includedByDefault: false,
        serviceId: serviceId,
        serviceName: service.name
      });
    },
    deactivate: _ref8 => {
      let {
        serviceId,
        service,
        onOptionalChange,
        logBI
      } = _ref8;
      onOptionalChange === null || onOptionalChange === void 0 || onOptionalChange(serviceId, false, false);
      if (!service) return;
      logBI === null || logBI === void 0 || logBI(10488, {
        optional: false,
        includedByDefault: false,
        serviceId: serviceId,
        serviceName: service.name
      });
    }
  },
  [AdvancedSetting.LimitCharges]: {
    activate: _ref9 => {
      let {
        serviceId,
        service,
        onLimitBillingTimesChange,
        logBI
      } = _ref9;
      onLimitBillingTimesChange === null || onLimitBillingTimesChange === void 0 || onLimitBillingTimesChange(serviceId, true, null);
      if (!service) return;
      logBI === null || logBI === void 0 || logBI(10489, {
        limit: true,
        count: serviceModel.maxChargesLimit(service),
        serviceId: service.id,
        serviceName: service.name
      });
    },
    deactivate: _ref10 => {
      let {
        serviceId,
        service,
        onLimitBillingTimesChange,
        logBI
      } = _ref10;
      onLimitBillingTimesChange === null || onLimitBillingTimesChange === void 0 || onLimitBillingTimesChange(serviceId, false, null);
      if (!service) return;
      logBI === null || logBI === void 0 || logBI(10489, {
        limit: false,
        count: serviceModel.maxChargesLimit(service),
        serviceId: service.id,
        serviceName: service.name
      });
    }
  },
  [AdvancedSetting.AutoPriceIncrease]: {
    activate: _ref11 => {
      let {
        service,
        serviceId,
        onAutoPriceIncreaseChange,
        now,
        relationshipState
      } = _ref11;
      if (!service || !now || !relationshipState) return;
      const increaseOn = defaultIncreaseOn(service, now);
      const validIncreaseOn = getValidIncreaseOn(increaseOn, now);
      const deafualtRoundPrices = defaultRoundPrices(service);
      const defaultPercentIncrease = defaultPrecentIncrease(service);
      const defaultIncreaseMethodValue = defaultIncreaseMethod(serviceModel.priceIncrease(service), relationshipState);
      const model = PriceIncreaseModel.make(deafualtRoundPrices, decimalFromStr(defaultPercentIncrease) || new Decimal(-1), defaultIncreaseMethodValue === IncreaseMethod.Date ? validIncreaseOn : null);
      onAutoPriceIncreaseChange === null || onAutoPriceIncreaseChange === void 0 || onAutoPriceIncreaseChange(serviceId, model);
    },
    deactivate: _ref12 => {
      let {
        serviceId,
        onAutoPriceIncreaseChange
      } = _ref12;
      return onAutoPriceIncreaseChange === null || onAutoPriceIncreaseChange === void 0 ? void 0 : onAutoPriceIncreaseChange(serviceId, PriceIncreaseModel.makeNone());
    }
  },
  [AdvancedSetting.QboClasses]: {
    activate: _ref13 => {
      let {
        serviceId,
        onQboClassChange
      } = _ref13;
      return onQboClassChange === null || onQboClassChange === void 0 ? void 0 : onQboClassChange(serviceId, serviceModel.makeIntegratedClassSet(''));
    },
    deactivate: _ref14 => {
      let {
        serviceId,
        onQboClassChange
      } = _ref14;
      return onQboClassChange === null || onQboClassChange === void 0 ? void 0 : onQboClassChange(serviceId, serviceModel.makeIntegratedClassUnset());
    }
  },
  [AdvancedSetting.FinancialCents]: {
    activate: _ref15 => {
      let {
        service,
        serviceId,
        onFinancialCentsChange
      } = _ref15;
      const ids = (service === null || service === void 0 ? void 0 : service.integrations.financialCentsIntegratedIds()) || null;
      const m = PracticeManagementServiceIntegrationModel.makeFinancialCents({
        integratedIds: ids,
        teamMembers: {},
        enabled: true
      });
      onFinancialCentsChange === null || onFinancialCentsChange === void 0 || onFinancialCentsChange(serviceId, m);
    },
    deactivate: _ref16 => {
      let {
        service,
        serviceId,
        onFinancialCentsChange
      } = _ref16;
      const ids = (service === null || service === void 0 ? void 0 : service.integrations.financialCentsIntegratedIds()) || null;
      const m = PracticeManagementServiceIntegrationModel.makeFinancialCents({
        integratedIds: ids,
        teamMembers: {},
        enabled: false
      });
      onFinancialCentsChange === null || onFinancialCentsChange === void 0 || onFinancialCentsChange(serviceId, m);
    }
  },
  [AdvancedSetting.Karbon]: {
    activate: _ref17 => {
      let {
        service,
        serviceId,
        onKarbonChange
      } = _ref17;
      const ids = (service === null || service === void 0 ? void 0 : service.integrations.karbonIntegratedIds()) || null;
      const m = PracticeManagementServiceIntegrationModel.makeKarbon({
        integratedIds: ids,
        teamMembers: {},
        enabled: true
      });
      onKarbonChange === null || onKarbonChange === void 0 || onKarbonChange(serviceId, m);
    },
    deactivate: _ref18 => {
      let {
        service,
        serviceId,
        onKarbonChange
      } = _ref18;
      const ids = (service === null || service === void 0 ? void 0 : service.integrations.karbonIntegratedIds()) || null;
      const m = PracticeManagementServiceIntegrationModel.makeKarbon({
        integratedIds: ids,
        teamMembers: {},
        enabled: false
      });
      onKarbonChange === null || onKarbonChange === void 0 || onKarbonChange(serviceId, m);
    }
  },
  [AdvancedSetting.LimitedTimeDiscountOffer]: {
    activate: _ref19 => {
      let {
        service,
        serviceId,
        onLimitedTimeDiscountOfferChange
      } = _ref19;
      if (!(service !== null && service !== void 0 && service.cost.discount)) return;
      const offer = getInitialLimitedTimeDiscountOffer();
      onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 || onLimitedTimeDiscountOfferChange(serviceId, offer);
    },
    deactivate: _ref20 => {
      let {
        serviceId,
        onLimitedTimeDiscountOfferChange
      } = _ref20;
      return onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 ? void 0 : onLimitedTimeDiscountOfferChange(serviceId, undefined);
    }
  },
  [AdvancedSetting.LimitedTimeDiscountOfferForAgreement]: {
    activate: _ref21 => {
      let {
        serviceId,
        onDiscountPeriodExpirationChange,
        now,
        isApprovedRelationship
      } = _ref21;
      if (!onDiscountPeriodExpirationChange) return;
      const expiration = getInitialDiscountPeriodExpiration({
        now: now !== null && now !== void 0 ? now : new Date(),
        isApprovedRelationship
      });
      onDiscountPeriodExpirationChange(serviceId, expiration);
    },
    deactivate: _ref22 => {
      let {
        serviceId,
        onDiscountPeriodExpirationChange
      } = _ref22;
      return onDiscountPeriodExpirationChange === null || onDiscountPeriodExpirationChange === void 0 ? void 0 : onDiscountPeriodExpirationChange(serviceId, undefined);
    }
  },
  [AdvancedSetting.PreapprovedAmount]: {
    activate: _ref23 => {
      let {
        serviceId,
        onPreapprovedAmountChange
      } = _ref23;
      return onPreapprovedAmountChange === null || onPreapprovedAmountChange === void 0 ? void 0 : onPreapprovedAmountChange(serviceId, null, true);
    },
    deactivate: _ref24 => {
      let {
        serviceId,
        onPreapprovedAmountChange
      } = _ref24;
      return onPreapprovedAmountChange === null || onPreapprovedAmountChange === void 0 ? void 0 : onPreapprovedAmountChange(serviceId, null, false);
    }
  },
  [AdvancedSetting.StartAutomaticBillingAt]: {
    activate: _ref25 => {
      let {
        service,
        onPausedChange,
        onBillOnAcceptanceChange,
        onStartBillingAtChange,
        now,
        relationshipEffectiveDate,
        isAmendedService
      } = _ref25;
      if (!service) return;
      const date = getInitialStartBillingAtDate({
        now,
        relationshipEffectiveDate,
        isAmendedService
      });
      updateStartBillingAtByMenuItem({
        item: StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate,
        value: date,
        service,
        onPausedChange,
        onBillOnAcceptanceChange,
        onStartBillingAtChange
      });
    },
    deactivate: _ref26 => {
      let {
        service,
        serviceId,
        onStartBillingAtChange,
        onPausedChange,
        onBillOnAcceptanceChange
      } = _ref26;
      if (!service) {
        onStartBillingAtChange === null || onStartBillingAtChange === void 0 || onStartBillingAtChange(serviceId, null);
        return;
      }
      if (serviceBillingModel.isPaused(service.billing)) {
        updateStartBillingAtByMenuItem({
          item: StartAutomaticBillingAtControlDropdownMenuItem.Pause,
          value: false,
          service,
          onPausedChange,
          onBillOnAcceptanceChange,
          onStartBillingAtChange
        });
        return;
      }
      if (serviceModel.startBillingAt(service)) {
        updateStartBillingAtByMenuItem({
          item: StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate,
          value: null,
          service,
          onPausedChange,
          onBillOnAcceptanceChange,
          onStartBillingAtChange
        });
        return;
      }
      if (serviceModel.isBillOnPeriodBeforeNextBilling(service)) {
        updateStartBillingAtByMenuItem({
          item: StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate,
          value: false,
          service,
          onPausedChange,
          onBillOnAcceptanceChange,
          onStartBillingAtChange
        });
        return;
      }
      onStartBillingAtChange === null || onStartBillingAtChange === void 0 || onStartBillingAtChange(serviceId, null);
    }
  }
};